import { useCallback, useMemo } from 'react'

import * as R from 'ramda'
import { useSelector } from 'react-redux'
import useSWR from 'swr'

import { User } from '@driftt/api-types'

import { fetchUserProperties, patchUserProperties } from 'api/customer'
import * as UserSelectors from 'modules/user/selectors'

const useShowTooltip = () => {
  const currentUser: User | undefined = useSelector(UserSelectors.currentUser)
  const userId = R.path(['id'], currentUser)

  const {
    data: properties = {},
    mutate,
    isValidating,
  } = useSWR([`/users/${userId}/properties`, true], fetchUserProperties, {
    revalidateOnFocus: false,
    refreshInterval: 0,
    dedupingInterval: 60000,
  })
  function objectIncludesKey(object, keyName) {
    return Object.keys(object).includes(keyName)
  }

  const findKeyByName = useCallback((object, keyName) => {
    return objectIncludesKey(object, keyName) && object[keyName]
  }, [])

  const tooltipDismissed: boolean = useMemo(
    () => findKeyByName(properties, 'hasDismissedSalesSetupReportNameTooltip'),
    [findKeyByName, properties]
  )

  const patchTooltipProperty = useCallback(
    async () =>
      patchUserProperties(userId, {
        ['hasDismissedSalesSetupReportNameTooltip']: true,
      })
        .then(({ data }) => {
          mutate(data)
        })
        .catch((err) => console.error(err)),
    [mutate, userId]
  )

  const neverShowTooltip = tooltipDismissed

  return useMemo(
    () => ({
      isValidating,
      neverShowTooltip,
      patchTooltipProperty,
    }),
    [isValidating, neverShowTooltip, patchTooltipProperty]
  )
}

export default useShowTooltip
